// ─── Intercompany Engine v2 — pure berekeningen + state-machines ───────────
//
// Geen Prisma-imports, geen node:* imports. Pure berekeningen + helpers die
// zowel in API als in de browser kunnen draaien.
//
// State-machines staan in @streep/types (één bron-van-waarheid); deze file
// re-exporteert ze voor het gemak en voegt berekenings-logica toe die niet
// in types-laag past.
//
// Spec: docs/PLAN-IC-ENGINE-EN-ONBOARDING-V2.md §2 + uploads/16-intercompany-engine 1.md.

import {
  IC_TRANSACTION_TRANSITION_MAP,
  IC_PERIOD_TRANSITION_MAP,
  isICTransactionTransitionAllowed,
  isICPeriodTransitionAllowed,
  type ICOpslagType,
  type ICStaffelEntry,
  type ICFeeMethode,
  type ICTransactionStatus,
} from '@streep/types'

// Re-export voor convenience — callers kunnen nu vanuit @streep/utils de hele
// IC-toolkit pakken.
export {
  IC_TRANSACTION_TRANSITION_MAP,
  IC_PERIOD_TRANSITION_MAP,
  isICTransactionTransitionAllowed,
  isICPeriodTransitionAllowed,
}

// IC-rule heeft geen multi-step state-machine — alleen actief/inactief
// toggle. Helper hier voor consistency.
export const IC_RULE_TRANSITION_MAP: Record<'ACTIEF' | 'INACTIEF', Array<'ACTIEF' | 'INACTIEF'>> = {
  ACTIEF: ['INACTIEF'],
  INACTIEF: ['ACTIEF'],
}

export function isICRuleTransitionAllowed(
  from: 'ACTIEF' | 'INACTIEF',
  to: 'ACTIEF' | 'INACTIEF',
): boolean {
  return from !== to
}

// ─────────────────────────────────────────────────────────────────────────
// Berekenfuncties per brontype.
// Alle bedragen zijn nummer (geen Prisma Decimal) — caller converteert.
// Returns altijd 2 decimalen.
// ─────────────────────────────────────────────────────────────────────────

function roundTwo(n: number): number {
  return Math.round(n * 100) / 100
}

export interface MedewerkerInzetInput {
  uren: number
  uurtarief: number
  opslagType?: ICOpslagType
  opslagPercentage?: number | null
  opslagVastBedrag?: number | null
  opslagStaffel?: ICStaffelEntry[] | null
}

export interface BerekenResultaat {
  subtotaal: number
  opslag: number
  totaal: number
}

/**
 * Medewerker-uitleen: subtotaal = uren × uurtarief, plus opslag.
 *
 * Opslag-strategieën (spec §7):
 * - GEEN: 0
 * - PERCENTAGE: subtotaal × percentage / 100
 * - VAST_BEDRAG: vast bedrag (los van uren)
 * - STAFFEL: pakt eerste staffel-entry waar subtotaal in [van, tot] valt
 */
export function berekenMedewerkerinzet(input: MedewerkerInzetInput): BerekenResultaat {
  const { uren, uurtarief, opslagType = 'GEEN', opslagPercentage, opslagVastBedrag, opslagStaffel } = input
  const subtotaal = roundTwo(uren * uurtarief)
  const opslag = berekenOpslag(subtotaal, opslagType, {
    percentage: opslagPercentage ?? null,
    vastBedrag: opslagVastBedrag ?? null,
    staffel: opslagStaffel ?? null,
  })
  return {
    subtotaal,
    opslag,
    totaal: roundTwo(subtotaal + opslag),
  }
}

export interface AutoInput {
  leaseprijs: number
  verzekeringPM?: number
  wegenbelastingPM?: number
  onderhoudsbudgetPM?: number
  /** Default 8% conform VoertuigToewijzing.interneOpslagPct. */
  opslagPct?: number
}

/**
 * Auto-doorbelasting per maand: leaseprijs + vaste maandkosten + opslag-pct
 * over alles. Zonder pro-rata; voor halve maand zie berekenAutoProRata.
 */
export function berekenAuto(input: AutoInput): BerekenResultaat {
  const { leaseprijs, verzekeringPM = 0, wegenbelastingPM = 0, onderhoudsbudgetPM = 0, opslagPct = 8 } = input
  const subtotaal = roundTwo(leaseprijs + verzekeringPM + wegenbelastingPM + onderhoudsbudgetPM)
  const opslag = roundTwo(subtotaal * (opslagPct / 100))
  return {
    subtotaal,
    opslag,
    totaal: roundTwo(subtotaal + opslag),
  }
}

/**
 * Pro-rata-bedrag voor een auto die maar een deel van de maand bij deze BV
 * hoort. Berekening: maandbedrag × (dagen-bij-deze-BV / dagen-in-maand).
 *
 * Maand-grenzen worden vastgepind op de 1e en de laatste dag van de maand
 * waarvan `periodeStart` de maand bepaalt. Als de toewijzing eerder/later loopt
 * dan de maand wordt geclamped binnen [maand-1e, maand-laatste].
 */
export function berekenAutoProRata(args: {
  maandBedrag: number
  periodeStart: Date // 1e van de maand (UTC)
  toewijzingStart: Date
  toewijzingEind?: Date | null
}): { dagen: number; dagenInMaand: number; bedrag: number } {
  const { maandBedrag, periodeStart, toewijzingStart, toewijzingEind } = args
  const jaar = periodeStart.getUTCFullYear()
  const maand = periodeStart.getUTCMonth()
  const eerste = new Date(Date.UTC(jaar, maand, 1))
  const laatste = new Date(Date.UTC(jaar, maand + 1, 0))
  const dagenInMaand = laatste.getUTCDate()

  const startMs = Math.max(toewijzingStart.getTime(), eerste.getTime())
  const eindMs = Math.min(
    (toewijzingEind ?? new Date(Date.UTC(2999, 0, 1))).getTime(),
    laatste.getTime(),
  )
  if (eindMs < startMs) {
    return { dagen: 0, dagenInMaand, bedrag: 0 }
  }
  const dagen = Math.round((eindMs - startMs) / (1000 * 60 * 60 * 24)) + 1
  const bedrag = roundTwo((maandBedrag * dagen) / dagenInMaand)
  return { dagen, dagenInMaand, bedrag }
}

export interface ManagementFeeInput {
  methode: ICFeeMethode
  vastBedrag?: number | null
  percentage?: number | null
  /** Bij methode=PERCENTAGE_OMZET: omzet-grondslag. */
  omzet?: number | null
  /** Bij methode=PERCENTAGE_KOSTEN: kosten-grondslag. */
  kosten?: number | null
  /** Verdeelsleutel: { bvId: pct } die optellen tot 100. Wordt gebruikt om totaal
   *  over BV's te verdelen. Voor één target-BV-aandeel wordt totaal × pct / 100 berekend. */
  verdeelsleutel?: Record<string, number> | null
  /** Bij STAFFEL_OMZET: staffel-tabel waar omzet doorheen-gerekend wordt. */
  staffel?: ICStaffelEntry[] | null
  /** Voor staffel/verdeelsleutel: omzet-totaal als input. */
  totaalGrondslag?: number | null
  /** Voor verdeelsleutel: doel-BV waarvoor we het aandeel berekenen. */
  targetBvId?: string
}

/**
 * Bereken één management-fee transactie.
 *
 * Acht methodes per spec §7:
 *  VAST_BEDRAG          → input.vastBedrag
 *  PERCENTAGE_OMZET     → omzet × percentage/100
 *  PERCENTAGE_KOSTEN    → kosten × percentage/100
 *  VERDEELSLEUTEL_FTE   → totaalGrondslag × verdeelsleutel[targetBvId]/100
 *  VERDEELSLEUTEL_UREN  → idem
 *  VERDEELSLEUTEL_OMZET → idem
 *  HANDMATIG_PER_BV     → caller geeft direct vastBedrag voor deze target-BV
 *  STAFFEL_OMZET        → staffel-tabel: pakt entry waar omzet in [van, tot]
 */
export function berekenManagementFee(input: ManagementFeeInput): BerekenResultaat {
  const { methode } = input
  let subtotaal = 0

  switch (methode) {
    case 'VAST_BEDRAG':
    case 'HANDMATIG_PER_BV':
      subtotaal = input.vastBedrag ?? 0
      break
    case 'PERCENTAGE_OMZET':
      subtotaal = ((input.omzet ?? 0) * (input.percentage ?? 0)) / 100
      break
    case 'PERCENTAGE_KOSTEN':
      subtotaal = ((input.kosten ?? 0) * (input.percentage ?? 0)) / 100
      break
    case 'VERDEELSLEUTEL_FTE':
    case 'VERDEELSLEUTEL_UREN':
    case 'VERDEELSLEUTEL_OMZET': {
      const { verdeelsleutel, totaalGrondslag, targetBvId } = input
      if (!verdeelsleutel || totaalGrondslag == null || !targetBvId) {
        subtotaal = 0
        break
      }
      const aandeelPct = verdeelsleutel[targetBvId] ?? 0
      subtotaal = (totaalGrondslag * aandeelPct) / 100
      break
    }
    case 'STAFFEL_OMZET': {
      const { staffel, omzet } = input
      if (!staffel || omzet == null) {
        subtotaal = 0
        break
      }
      const entry = staffel.find((s) => omzet >= s.van && omzet < s.tot)
      if (!entry) {
        subtotaal = 0
      } else if (entry.vastBedrag != null) {
        subtotaal = entry.vastBedrag
      } else if (entry.percentage != null) {
        subtotaal = (omzet * entry.percentage) / 100
      }
      break
    }
  }

  subtotaal = roundTwo(subtotaal)
  // Geen aparte opslag in mgmt-fee — die komt via een aparte IntercompanyMarkup-rij
  // in latere fasen. Hier alleen subtotaal == totaal.
  return { subtotaal, opslag: 0, totaal: subtotaal }
}

export interface TankpasInput {
  totaalBrandstof: number
  /** Privégebruik percentage dat NIET doorbelast wordt. Default 0. */
  privePercentage?: number
  /** Opslag-percentage voor administratie-handling op tankpas. Default 0. */
  opslagPct?: number
}

/**
 * Tankpas-doorbelasting: totaal × (1 - prive%) + opslag%.
 */
export function berekenTankpas(input: TankpasInput): BerekenResultaat {
  const { totaalBrandstof, privePercentage = 0, opslagPct = 0 } = input
  const subtotaal = roundTwo(totaalBrandstof * (1 - privePercentage / 100))
  const opslag = roundTwo(subtotaal * (opslagPct / 100))
  return { subtotaal, opslag, totaal: roundTwo(subtotaal + opslag) }
}

// ─────────────────────────────────────────────────────────────────────────
// Hulp: opslag-engine.
// ─────────────────────────────────────────────────────────────────────────

export function berekenOpslag(
  subtotaal: number,
  opslagType: ICOpslagType,
  opts: {
    percentage?: number | null
    vastBedrag?: number | null
    staffel?: ICStaffelEntry[] | null
  },
): number {
  switch (opslagType) {
    case 'GEEN':
      return 0
    case 'PERCENTAGE':
      return roundTwo(subtotaal * ((opts.percentage ?? 0) / 100))
    case 'VAST_BEDRAG':
      return roundTwo(opts.vastBedrag ?? 0)
    case 'STAFFEL': {
      const staffel = opts.staffel ?? []
      const entry = staffel.find((s) => subtotaal >= s.van && subtotaal < s.tot)
      if (!entry) return 0
      if (entry.vastBedrag != null) return roundTwo(entry.vastBedrag)
      if (entry.percentage != null) return roundTwo(subtotaal * (entry.percentage / 100))
      return 0
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────
// BTW-helper.
// ─────────────────────────────────────────────────────────────────────────

export function berekenBtw(
  subtotaalIncOpslag: number,
  btwPercentage: number,
): { btwBedrag: number; totaalIncBtw: number } {
  const btwBedrag = roundTwo(subtotaalIncOpslag * (btwPercentage / 100))
  return { btwBedrag, totaalIncBtw: roundTwo(subtotaalIncOpslag + btwBedrag) }
}

// ─────────────────────────────────────────────────────────────────────────
// Code-generator IC-YYYY-MM-NNNN.
// Per maand sequentieel zodat code-collisies onmogelijk zijn binnen één maand
// zelfs als de DB-uniqueness constraint zou falen.
// ─────────────────────────────────────────────────────────────────────────

export function buildICTransactionCode(jaar: number, maand: number, n: number): string {
  const next = n + 1
  const mm = String(maand).padStart(2, '0')
  const padded = String(next).padStart(4, '0')
  return `IC-${jaar}-${mm}-${padded}`
}

export function parseICTransactionCodeNumber(code: string): {
  jaar: number
  maand: number
  n: number
} | null {
  const match = code.match(/^IC-(\d{4})-(\d{2})-(\d+)$/)
  if (!match) return null
  return {
    jaar: Number.parseInt(match[1] ?? '0', 10),
    maand: Number.parseInt(match[2] ?? '0', 10),
    n: Number.parseInt(match[3] ?? '0', 10),
  }
}

export function isTerminalICTransactionStatus(status: ICTransactionStatus): boolean {
  return IC_TRANSACTION_TRANSITION_MAP[status].length === 0
}
